import { getSupabaseClient, type DdmSupabaseClient } from '../../supabase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DeleteAccountError = 'unauthenticated' | 'already_deleted' | 'internal_error';

export class DeleteAccountFailure extends Error {
  constructor(
    public readonly errorCode: DeleteAccountError,
    message?: string,
  ) {
    super(message ?? errorCode);
    this.name = 'DeleteAccountFailure';
  }
}

// ---------------------------------------------------------------------------
// requestAccountDeletion — invoke Edge `delete-account-request`
// ---------------------------------------------------------------------------
// The Edge function (admin verify_jwt=true) propagates the user JWT to the
// SECURITY DEFINER RPC `request_account_deletion(p_user_id)`. The RPC:
//   1. Soft-deletes the user (status='disabled', deleted_at=NOW(), email_verified=false)
//   2. Anonymises PII (email → deleted-{id}@anonymised.local, first_name/last_name/phone NULL)
//   3. Audit-logs the action with original_snapshot for manual V2 restore (30j window)
//   4. Wipes saved_deals (RGPD strict, ADR-025 §E pgTAP T5)
//
// Post-success: the caller signs out and redirects to `/`. The user can no
// longer log back in (AuthContext checks `deleted_at IS NULL`).
//
// Errors:
//   - 401 → unauthenticated  (JWT missing or expired)
//   - 409 → already_deleted  (idempotent guard)
//   - 500 → internal_error
//
// Resolves void on 200 success.

export async function requestAccountDeletion(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { data, error } = await client.functions.invoke<{
    success: boolean;
    code?: DeleteAccountError;
    message?: string;
  }>('delete-account-request', {
    body: {},
  });

  if (error) {
    const ctx = (error as { context?: { json?: () => Promise<unknown> } }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const payload = (await ctx.json()) as { code?: DeleteAccountError };
        if (payload?.code) {
          throw new DeleteAccountFailure(payload.code, error.message);
        }
      } catch (parseErr) {
        if (parseErr instanceof DeleteAccountFailure) throw parseErr;
      }
    }
    throw new DeleteAccountFailure('internal_error', error.message);
  }

  if (!data || data.success !== true) {
    throw new DeleteAccountFailure(
      data?.code ?? 'internal_error',
      data?.message ?? 'unexpected_response',
    );
  }
}
